import { z } from 'zod';

/** Superfans custom-block enums, as Hermes accepts them. */
export const BLOCK_TYPES = [
  'EMBEDDED',
  'FULLSCREEN',
  'IMMERSIVE',
  'POPUPBOTTOM',
  'POPUPCENTER',
] as const;
export const BLOCK_PAGES = ['HOME', 'PLP', 'PDP', 'CART', 'ACCOUNT', 'ALL'] as const;
export const COLLAPSE_TYPES = ['COLLAPSENONE', 'COLLAPSECLOSE', 'COLLAPSENODISMISS'] as const;
export const INPUT_FIELD_TYPES = [
  'text',
  'dropdown',
  'product',
  'collection',
  'dynamic',
  'upload',
] as const;

export type BlockType = (typeof BLOCK_TYPES)[number];
export type BlockPage = (typeof BLOCK_PAGES)[number];
export type CollapseType = (typeof COLLAPSE_TYPES)[number];

/** One merchant-editable field. Type-specific keys (options, apiUrl, …) pass through unchanged. */
export const inputFieldSchema = z.looseObject({
  type: z.enum(INPUT_FIELD_TYPES),
  title: z.string().min(1).max(200),
  uniqueId: z.string().regex(/^[a-z0-9_-]{1,100}$/i, 'use letters, digits, - and _ only'),
});
export type InputField = z.infer<typeof inputFieldSchema>;

const CODE_MAX = 500_000;
const code = z.string().max(CODE_MAX);

const inputFieldsSchema = z
  .array(inputFieldSchema)
  .max(50)
  .superRefine((fields, ctx) => {
    const seen = new Set<string>();
    fields.forEach((f, i) => {
      if (seen.has(f.uniqueId)) {
        ctx.addIssue({
          code: 'custom',
          path: [i, 'uniqueId'],
          message: `duplicate uniqueId "${f.uniqueId}"`,
        });
      }
      seen.add(f.uniqueId);
    });
  });

/** The editable parts of a block: the editor tabs plus its input fields. */
export const blockFilesSchema = z.object({
  html: code,
  css: code,
  js: code,
  head: code,
  inputFields: inputFieldsSchema,
});
export type BlockFiles = z.infer<typeof blockFilesSchema>;

export const BLOCK_FILE_KEYS = ['html', 'css', 'js', 'head', 'inputFields'] as const;

export const EMPTY_BLOCK_FILES: BlockFiles = {
  html: '',
  css: '',
  js: '',
  head: '',
  inputFields: [],
};

/** What the agent returns in a `superfans-block` fence: any subset of the files. */
export const blockPatchSchema = blockFilesSchema
  .partial()
  .strict()
  .refine((patch) => Object.keys(patch).length > 0, 'The code block changes nothing');
export type BlockPatch = z.infer<typeof blockPatchSchema>;

export const blockMetaSchema = z
  .object({
    name: z.string().trim().min(1, 'Name is required').max(200),
    type: z.enum(BLOCK_TYPES),
    segregationType: z.enum(BLOCK_PAGES),
    collapseType: z.enum(COLLAPSE_TYPES).optional(),
  })
  .refine((m) => !(m.type === 'EMBEDDED' && m.segregationType === 'ALL'), {
    path: ['segregationType'],
    message: 'Embedded blocks need a specific page, not All pages',
  });
export type BlockMeta = z.infer<typeof blockMetaSchema>;

/** Body of POST /api/blocks-studio/agent/chat. */
/**
 * A saved block's id. It is forwarded as the `x-conversation-scope` header, so it is limited to
 * header-safe characters.
 */
export const blocksStudioBlockIdSchema = z.string().regex(/^[A-Za-z0-9_.:-]{1,200}$/);

export const blocksStudioChatRequestSchema = z.object({
  message: z.string().trim().min(1).max(20_000),
  attachments: z
    .array(z.object({ fileId: z.string().min(1).max(200) }))
    .max(10)
    .default([]),
  threadId: z.string().min(1).max(200).optional(),
  model: z.string().min(1).max(100).optional(),
  /** The saved block the chat belongs to; null for an unsaved draft. Scopes its conversations. */
  blockId: blocksStudioBlockIdSchema.nullable().optional(),
  /** Continues that conversation; omit (or null) to start a new one. */
  conversationId: z.uuid().nullable().optional(),
  /**
   * Files that changed since the block the agent last saw in this conversation; only these are
   * sent to the agent ([] = nothing changed). Omitted: the agent gets the whole block.
   */
  changedFiles: z.array(z.enum(BLOCK_FILE_KEYS)).max(BLOCK_FILE_KEYS.length).optional(),
  block: z.object({
    id: z.string().min(1).max(100).optional(),
    name: z.string().max(200),
    type: z.enum(BLOCK_TYPES),
    segregationType: z.enum(BLOCK_PAGES),
    files: blockFilesSchema,
  }),
});
export type BlocksStudioChatRequest = z.infer<typeof blocksStudioChatRequestSchema>;

/** Query of the Blocks Studio conversation routes: the block whose conversations (absent: draft). */
export const blocksStudioConversationQuerySchema = z.object({
  blockId: blocksStudioBlockIdSchema.optional(),
});
export type BlocksStudioConversationQuery = z.infer<typeof blocksStudioConversationQuerySchema>;

/** Body of POST /api/blocks-studio/agent/conversations/:id/adopt: the saved block taking a draft chat. */
export const blocksStudioAdoptConversationSchema = z.object({ blockId: blocksStudioBlockIdSchema });
export type BlocksStudioAdoptConversation = z.infer<typeof blocksStudioAdoptConversationSchema>;

/** Query of GET /api/blocks-studio/agent/conversations. */
export const blocksStudioConversationListQuerySchema = blocksStudioConversationQuerySchema.extend({
  limit: z.coerce.number().int().min(1).max(100).optional(),
  cursor: z.uuid().optional(),
});
export type BlocksStudioConversationListQuery = z.infer<
  typeof blocksStudioConversationListQuerySchema
>;

/** GET /api/blocks-studio/config. */
export const blocksStudioConfigSchema = z.object({
  enabled: z.boolean(),
  models: z.array(z.string()),
});
export type BlocksStudioConfigDto = z.infer<typeof blocksStudioConfigSchema>;

/** POST /api/blocks-studio/agent/files (relayed from the gateway). */
export const blocksStudioFileSchema = z.object({
  fileId: z.string(),
  name: z.string(),
  mediaType: z.string(),
  sizeBytes: z.number(),
});
export type BlocksStudioFile = z.infer<typeof blocksStudioFileSchema>;

/** A multiple-choice question the agent asks in a `superfans-question` fence. */
export const agentQuestionSchema = z.object({
  text: z.string().min(1).max(500),
  options: z
    .array(
      z.object({
        label: z.string().min(1).max(200),
        description: z.string().max(300).optional(),
      }),
    )
    .min(2)
    .max(6),
  /** Several options may be picked; a single-choice question is the default. */
  multiple: z.boolean().optional(),
});
export const agentQuestionsSchema = z.object({
  questions: z.array(agentQuestionSchema).min(1).max(4),
});
export type AgentQuestion = z.infer<typeof agentQuestionSchema>;
export type AgentQuestions = z.infer<typeof agentQuestionsSchema>;
